import React, { FC, useState, useCallback } from 'react'
import { Box, Typography, IconButton, TextField, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useToast } from '@/shared/contexts/ToastContext'

interface MemoTabProps {
  memoItems: string[]
  onUpdateMemo?: (memoItems: string[]) => void
  isUpdating?: boolean
  /** セクションタイトル（指定時はタイトル行にアクションボタンを統合表示） */
  title?: string
}

const MemoTab: FC<MemoTabProps> = ({
  memoItems,
  onUpdateMemo,
  isUpdating = false,
  title,
}) => {
  const { showToast } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [editItems, setEditItems] = useState<string[]>([])
  const [newItem, setNewItem] = useState('')

  const handleStartEdit = useCallback(() => {
    setEditItems([...memoItems])
    setNewItem('')
    setIsEditing(true)
  }, [memoItems])

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditItems([])
    setNewItem('')
  }, [])

  const handleSaveEdit = useCallback(() => {
    const items = newItem.trim()
      ? [...editItems, newItem.trim()]
      : [...editItems]
    onUpdateMemo?.(items)
    setIsEditing(false)
    setEditItems([])
    setNewItem('')
  }, [editItems, newItem, onUpdateMemo])

  const handleAddItem = useCallback(() => {
    if (newItem.trim()) {
      setEditItems((prev) => [...prev, newItem.trim()])
      setNewItem('')
    }
  }, [newItem])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
        e.preventDefault()
        handleAddItem()
      }
    },
    [handleAddItem]
  )

  const handleDeleteItem = useCallback((index: number) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleEditItem = useCallback((index: number, value: string) => {
    setEditItems((prev) => prev.map((item, i) => (i === index ? value : item)))
  }, [])

  const handleCopy = useCallback(() => {
    const text = memoItems.map((item) => `・${item}`).join('\n')
    navigator.clipboard.writeText(text)
    showToast('メモをコピーしました', 'success', 2000)
  }, [memoItems, showToast])

  const actionButtons = isEditing ? (
    <>
      <Tooltip title="保存">
        <IconButton
          size="small"
          onClick={handleSaveEdit}
          disabled={isUpdating}
          sx={{ color: colorTokens.blue[600] }}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="キャンセル">
        <IconButton size="small" onClick={handleCancelEdit}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  ) : (
    <>
      {onUpdateMemo && (
        <Tooltip title="編集">
          <IconButton size="small" onClick={handleStartEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {memoItems.length > 0 && (
        <Tooltip title="コピー">
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  )

  return (
    <Box>
      {/* タイトル行（title指定時はタイトル+ボタン一体） */}
      {title ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 0.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: colorTokens.grey[700],
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
            }}
          >
            {actionButtons}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            mb: 1,
            gap: 0.5,
          }}
        >
          {actionButtons}
        </Box>
      )}

      {/* メモ一覧 */}
      {isEditing ? (
        <Box>
          {editItems.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.25,
                gap: 0.5,
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={item}
                onChange={(e) => handleEditItem(index, e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" onClick={() => handleDeleteItem(index)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
          <TextField
            fullWidth
            size="small"
            placeholder="新しいメモを入力..."
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ mt: 0.5 }}
          />
        </Box>
      ) : memoItems.length > 0 ? (
        <Box>
          {memoItems.map((item, index) => (
            <Typography
              key={index}
              sx={{
                color: colorTokens.text.primary,
                py: 0.25,
              }}
            >
              ・{item}
            </Typography>
          ))}
        </Box>
      ) : (
        <Typography
          sx={{
            color: colorTokens.text.secondary,
            py: 0.5,
          }}
        >
          メモはありません
        </Typography>
      )}
    </Box>
  )
}

export default MemoTab
